"use strict";

define('components/filter_bar',[
    'components/filter_bar/filters/alpha_filters',
    'components/filter_bar/filters/datepicker_filters',
    'components/filter_bar/filters/dropdown_filters',
    'components/filter_bar/filters/extension_field_filters',
    'components/filter_bar/filters/link_filters',
    'components/filter_bar/filters/location_filters',
    'components/filter_bar/filters/select2_filters'
], function(
    alphaFiltersComponent,
    datePickerFiltersComponent,
    dropdownFiltersComponent,
    extensionFieldFiltersComponent,
    linkFiltersComponent,
    locationFiltersComponent,
    select2FiltersComponent
) {
    // This is the abstract filter bar component,
    // page should use specific implementations such as tableFilterBarComponent or activityFilterBarComponent

    var filterBarComponent = {
        init: function(containerNode) {
            var buttonbarNode = containerNode.find('div.buttonbar');
            if (!buttonbarNode[0]) return;

            // `context` is used to scope the filterBarComponent and its related components
            // to this particular `buttonbarNode`.
            var context = _getContext(containerNode, buttonbarNode);

            // Filters and UI that are common for all filterBars can go here.
            // Special-purpose ones do not belong here.

            alphaFiltersComponent.initWithContext(context);
            datePickerFiltersComponent.initWithContext(context);
            dropdownFiltersComponent.initWithContext(context);
            extensionFieldFiltersComponent.initWithContext(context);
            linkFiltersComponent.initWithContext(context);
            locationFiltersComponent.initWithContext(context);
            select2FiltersComponent.initWithContext(context);

            // Updates text inputs when blurred.
            // `select2-input` and `location-search-bar`
            // are handled by their own components.
            buttonbarNode.find('input:text').blur(function() {
                var input = $(this);

                if (input.is('.select2-input, .select2-offscreen, .datepicker-field')) return;

                var value = input.val().trim();
                var filterName = input.attr('id');

                // The locationFiltersComponent will take care of this.
                if (filterName === 'location-search-bar') return;

                context.applyTextFilter(filterName, value);
                context.markDirtyState();
            });
            return context;
        },

        resetToDefaultUI: function(context) {
            context.buttonbarNode.find('input:text').val('');

            dropdownFiltersComponent.resetToDefaultUI(context);
            linkFiltersComponent.resetToDefaultUI(context);
            locationFiltersComponent.resetToDefaultUI(context);
            select2FiltersComponent.resetToDefaultUI(context);
        }
    };

    function _getContext(containerNode, buttonbarNode) {
        var context = buttonbarNode.data('filterBarContext');

        if (!context) {
            context = _makeContext(containerNode, buttonbarNode);
            buttonbarNode.data('filterBarContext', context);
        }

        return context;
    }

    function _makeContext(containerNode, buttonbarNode) {
        var context = {
            containerNode: containerNode,
            buttonbarNode: buttonbarNode,

            _sortOrder: null,
            _filters: null,

            // There are three possible quantifiers: any, all, none
            _filterQuantifiers: {},

            _baseUrl: null,

            // Additional components may add other data to this context object.
            // A good pattern is `context.{componentName}Data = {..}`.
            // Examples:
            //   tableFilterBarComponent:  context.tableFilterBarData = { tableId: 'abc' }
            //   urlStorageComponent:      context.urlStorageData = { example: ... }
        };

        context.isInitialized = function() {
            return context._filters !== null;
        };

        context.markDirtyState = function() {
            context.pushState({});
        };

        // Pushing a "state" is only useful in conjunction
        // with window.History as used in storage/url_storage.js
        // Other components can just ignore `data` and `url`;
        // interpret the jsq:filterBarDirtyState event as a
        // good time to perform ajax requests
        // Register via context.onDirtyState()
        // We use dirtyStateTimer to debounce intermediate state changes.
        // NOTE: 200ms timeout is needed, multiple input change events
        // need a bit of time to queue up; if we set the timeout too low
        // the debounce is ineffective.
        var dirtyStateTimer = null;
        context.pushState = function(stateData, url) {
            clearTimeout(dirtyStateTimer);
            dirtyStateTimer = setTimeout(function() {
                stateData.component = 'buttonbar';
                buttonbarNode.trigger('jsq:filterBarDirtyState', stateData, url);
            }, 200);
        };

        context.onDirtyState = function(handler) {
            buttonbarNode.on('jsq:filterBarDirtyState', handler);
        };

        // jsq:filterBarStateSaved indicates a good time to update views
        // Register via context.onStateSaved()
        context.markStateSaved = function() {
            buttonbarNode.trigger('jsq:filterBarStateSaved');
        };

        context.onStateSaved = function(handler) {
            // Debounce by 750ms
            var stateChangeTimeout = null;
            var executeHandler = function() {
                handler();
                stateChangeTimeout = null;
            }
            buttonbarNode.on('jsq:filterBarStateSaved', function() {
                if (stateChangeTimeout) {
                    clearTimeout(stateChangeTimeout);
                    stateChangeTimeout = null;
                }
                stateChangeTimeout = setTimeout(executeHandler, 750);
            });
        };

        context.hasSortOrder = function() {
            if (!context._sortOrder) return false;

            return context._sortOrder.length == 2;
        };

        context.getSortOrder = function() {
            return context._sortOrder;
        };

        // sortOrder: [sortKey, sortDir]
        context.setSortOrder = function(sortOrder) {
            context._sortOrder = sortOrder;
        };

        context.hasAnyFilters = function() {
            if (!context._filters) return false;

            return !$.isEmptyObject(context._filters);
        };

        context.hasFilter = function(filter) {
            if (!context._filters) return false;

            var pair = filter.split('=');
            return Object.prototype.hasOwnProperty.call(context._filters, pair[0]);
        };

        context.forEachFilter = function(perItem) {
            var filters = context._filters;
            if (!filters) return;

            for (var filterName in filters) {
                if (Object.prototype.hasOwnProperty.call(filters, filterName)) {
                    perItem(filterName, filters[filterName]);
                }
            }
        };

        context.getFilter = function(filterName) {
            if (!context._filters) return null;

            return context._filters[filterName];
        };

        context.applyFilter = function(filterName, value, options) {
            if (!context._filters) {
                context._filters = {};
            }

            // Absence of a value indicates key removal
            if (!value) {
                if (Object.prototype.hasOwnProperty.call(context._filters, filterName)) {
                    delete context._filters[filterName];
                }
                return;
            }

            var replaceAll = options && options.replaceAll;

            if (!Object.prototype.hasOwnProperty.call(context._filters, filterName) || replaceAll) {
                context._filters[filterName] = [];
            }

            var values = typeof value === 'string' ? value.split('~') : [value];
            values.forEach(function addValue(value) {
                if (context._filters[filterName].indexOf(value) === -1) {
                    context._filters[filterName].push(value);
                }
            });
        };

        context.applyFilterFromString = function(str, options) {
            var pair = str.split('=');
            return context.applyFilter(pair[0], pair[1], options);
        };

        context.applyTextFilter = function(filterName, value) {
            if (value.length > 0) {
                context.applyFilter(filterName, value, {replaceAll: true});
            } else if (context.hasFilter(filterName)) {
                context.removeFilter(filterName);
            }
        };

        context.removeFilter = function(filter) {
            var _pair$;
            var pair = filter.split('=');
            var filterName = pair[0];
            // note: this is equivalent to `pair[1]?.split('~')`
            var values = (_pair$ = pair[1]) === null || _pair$ === void 0 ? void 0 : _pair$.split('~');

            if (!context._filters) return;
            if (!Object.prototype.hasOwnProperty.call(context._filters, filterName)) return;

            // If there is no value, remove the entire key
            if (pair.length == 1) {
                delete context._filters[filterName];
                return;
            }

            values.forEach(function removeValue(value){
                // Find the value in this key and remove it
                var filterValueIdx = context._filters[filterName].indexOf(value);
                if (filterValueIdx < 0) return;
                context._filters[filterName].splice(filterValueIdx, 1);
            });
            // Remove the filter key if it no longer has any values
            if (context._filters[filterName].length == 0) {
                delete context._filters[filterName];
            }
        };

        context.clearFilters = function() {
            context._filters = {};
        };

        context.forEachFilterQuantifier = function(perItem) {
            var quantifiers = context._filterQuantifiers;
            if (!quantifiers) return;

            for (var filterName in quantifiers) {
                if (Object.prototype.hasOwnProperty.call(quantifiers, filterName)) {
                    perItem(filterName, quantifiers[filterName]);
                }
            }
        };

        context.getFilterQuantifier = function(filterName) {
            return context._filterQuantifiers[filterName];
        };

        context.setFilterQuantifier = function(filterName, quantifier) {
            context._filterQuantifiers[filterName] = quantifier;
        };

        context.hasBaseUrl = function() {
            return !!context._baseUrl;
        };

        context.getBaseUrl = function() {
            return context._baseUrl;
        };

        context.setBaseUrl = function(baseUrl) {
            context._baseUrl = baseUrl;
        };

        context.getParams = function() {
            return {
                sortOrder: context._sortOrder,
                filters: context._filters,
                filterQuantifiers: context._filterQuantifiers,
                baseUrl: context._baseUrl,
            };
        };

        context.setParams = function(params) {
            context._sortOrder = (params && params.sortOrder) || [];
            context._filters = (params && params.filters) || {};
            context._filterQuantifiers = (params && params.filterQuantifiers) || {};
            context._baseUrl = params && params.baseUrl;
        };

        return context;
    }

    return filterBarComponent;
});

